/**
 * Deals Table Columns Configuration
 *
 * Defines table column structure, cell renderers, and sorting logic
 * for the Deals list view table layout.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
  StatusBadge,
  CategoryIcon,
  ProgressBadge,
} from "../../ui/StatusIndicators";

// Helper function to format currency
const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format percentages
const formatPercentage = (value) => {
  if (!value) return "-";
  return `${value}%`;
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper function to get deal category
const getDealCategory = (deal) => {
  return deal.type?.toLowerCase() || "acquisition";
};

const DealsColumns = [
  {
    key: "name",
    title: "Deal Name",
    width: "25%",
    sortable: true,
    render: (value, deal) => (
      <div className="flex items-center space-x-3">
        <CategoryIcon category={getDealCategory(deal)} size="sm" />
        <div>
          <Link
            to={`/deal/${deal.id}`}
            className="font-medium text-gray-900 hover:text-indigo-600"
          >
            {value || "Untitled Deal"}
          </Link>
          {deal.description && (
            <p className="text-sm text-gray-500 truncate max-w-xs">
              {deal.description}
            </p>
          )}
        </div>
      </div>
    ),
  },

  {
    key: "status",
    title: "Status",
    width: "12%",
    sortable: true,
    render: (value, deal) => (
      <StatusBadge status={value || "draft"} type="status" />
    ),
  },

  {
    key: "type",
    title: "Type",
    width: "15%",
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700 capitalize">
        {value?.replace("-", " ") || "Acquisition"}
      </span>
    ),
  },

  {
    key: "financial.totalDealValue",
    title: "Deal Value",
    width: "15%",
    sortable: true,
    render: (value, deal) => (
      <span className="text-sm font-medium text-gray-900">
        {formatCurrency(deal.financial?.totalDealValue)}
      </span>
    ),
  },

  {
    key: "financial.irr",
    title: "IRR",
    width: "10%",
    sortable: true,
    render: (value, deal) => {
      const irr = deal.financial?.irr;
      return (
        <span className="text-sm font-medium text-gray-900">
          {formatPercentage(irr)}
        </span>
      );
    },
  },

  {
    key: "terms.completionDate",
    title: "Completion",
    width: "12%",
    sortable: true,
    render: (value, deal) => (
      <span className="text-sm text-gray-700">
        {formatDate(deal.terms?.completionDate)}
      </span>
    ),
  },

  {
    key: "relatedProject",
    title: "Project",
    width: "11%",
    sortable: false,
    render: (value, deal) => {
      if (!value) return <span className="text-sm text-gray-400">-</span>;

      return (
        <Link
          to={`/project/${value}`}
          className="text-sm text-indigo-600 hover:text-indigo-700 underline"
        >
          {deal.projectMetadata?.projectName || value}
        </Link>
      );
    },
  },
];

export default DealsColumns;
